import {
  getDefaultThemeModeId,
  getThemeModeDefinition,
  isThemeModeId,
  validateThemeTokens,
  THEME_TOKEN_KEYS
} from '../../../../../src/ui/theme_modes.js';
import { resolveThemeRef } from '../../../../../src/ui/runtime/ui-resolution.js';
import {
  registerThemeConsumer,
  unregisterThemeConsumer
} from '../../../../../src/ui/theme_consumers.js';

export const SETTINGS_THEME_STORAGE_KEY = 'kg.settings.theme-mode.v1';

const SETTINGS_THEME_CONSUMER_ID = 'plugin.platform.settings';
const SETTINGS_THEME_SCOPE_ID = 'settings-only';

const BASE_SETTINGS_THEME_VAR_KEYS = Object.freeze({
  bg: '--kg-settings-bg',
  surface0: '--kg-settings-surface-0',
  surface1: '--kg-settings-surface-1',
  surface2: '--kg-settings-surface-2',
  surface3: '--kg-settings-surface-3',
  border: '--kg-settings-border',
  text: '--kg-settings-text',
  textSubtle: '--kg-settings-text-subtle',
  primary: '--kg-settings-primary',
  primaryHover: '--kg-settings-primary-hover',
  primaryActive: '--kg-settings-primary-active',
  primaryText: '--kg-settings-primary-text',
  danger: '--kg-settings-danger',
  dangerHover: '--kg-settings-danger-hover',
  dangerActive: '--kg-settings-danger-active',
  dangerText: '--kg-settings-danger-text',
  controlSurface: '--kg-settings-control-surface',
  controlBorder: '--kg-settings-control-border',
  controlText: '--kg-settings-control-text',
  controlPlaceholder: '--kg-settings-control-placeholder',
  pillDefaultBg: '--kg-settings-pill-default-bg',
  pillDefaultBorder: '--kg-settings-pill-default-border',
  pillDefaultText: '--kg-settings-pill-default-text',
  pillPositiveBg: '--kg-settings-pill-positive-bg',
  pillPositiveBorder: '--kg-settings-pill-positive-border',
  pillPositiveText: '--kg-settings-pill-positive-text',
  pillWarningBg: '--kg-settings-pill-warning-bg',
  pillWarningBorder: '--kg-settings-pill-warning-border',
  pillWarningText: '--kg-settings-pill-warning-text',
  pillCurrentBg: '--kg-settings-pill-current-bg',
  pillCurrentBorder: '--kg-settings-pill-current-border',
  pillCurrentText: '--kg-settings-pill-current-text',
  tableHeaderBg: '--kg-settings-table-header-bg',
  tableHeaderText: '--kg-settings-table-header-text',
  tableRowBorder: '--kg-settings-table-row-border',
  modalSurface: '--kg-settings-modal-surface',
  modalBorder: '--kg-settings-modal-border',
  modalText: '--kg-settings-modal-text',
  shadowCard: '--kg-settings-shadow-card',
  shadowControlIdle: '--kg-settings-shadow-control-idle',
  shadowControlHover: '--kg-settings-shadow-control-hover',
  shadowControlActive: '--kg-settings-shadow-control-active',
  transitionFast: '--kg-settings-transition-fast',
  transitionDefault: '--kg-settings-transition-default',
  focusRing: '--kg-settings-focus-ring',
  surface: '--kg-settings-surface',
  highlight: '--kg-settings-highlight',
  innerHighlight: '--kg-settings-inner-highlight',
  text2: '--kg-settings-text-2',
  muted: '--kg-settings-muted-tone',
  accent: '--kg-settings-accent',
  accentSoft: '--kg-settings-accent-soft',
  warning: '--kg-settings-warning',
  success: '--kg-settings-success',
  focus: '--kg-settings-focus',
  shadow1: '--kg-settings-shadow-1',
  shadow2: '--kg-settings-shadow-2',
  shadow3: '--kg-settings-shadow-3',
  glowSoft: '--kg-settings-glow-soft',
  overlayScrim: '--kg-settings-overlay-scrim',
  overlaySurface: '--kg-settings-overlay-surface',
  overlayBorder: '--kg-settings-overlay-border',
  overlayShadow: '--kg-settings-overlay-shadow',
  overlayText: '--kg-settings-overlay-text',
  overlayText2: '--kg-settings-overlay-text-2',
  overlayFocus: '--kg-settings-overlay-focus',
  easeStandard: '--kg-settings-ease-standard',
  durFast: '--kg-settings-dur-fast',
  durMed: '--kg-settings-dur-med'
});

const SETTINGS_THEME_VAR_KEYS = Object.freeze({
  ...BASE_SETTINGS_THEME_VAR_KEYS,
  ...buildSettingsToneVarKeys(),
  ...buildSettingsAccentVarKeys()
});

function buildSettingsToneVarKeys() {
  const toneIds = ['neutral', 'brand', 'success', 'warning', 'danger', 'info'];
  const stateIds = ['bg', 'border', 'text', 'hover', 'active', 'focus'];
  const out = {};
  toneIds.forEach((toneId) => {
    stateIds.forEach((stateId) => {
      out[`tone.${toneId}.${stateId}`] = `--kg-settings-tone-${toneId}-${stateId}`;
    });
  });
  return out;
}

function buildSettingsAccentVarKeys() {
  const out = {};
  for (let hue = 1; hue <= 8; hue += 1) {
    const hueId = String(hue).padStart(2, '0');
    for (let step = 1; step <= 6; step += 1) {
      const stepId = String(step).padStart(2, '0');
      out[`accent.h${hueId}.s${stepId}`] = `--kg-settings-accent-h${hueId}-s${stepId}`;
    }
  }
  return out;
}

function safeLocalStorage() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage || null;
  } catch {
    return null;
  }
}

function createDefaultSettingsThemeState() {
  return {
    version: 1,
    themeId: getDefaultThemeModeId(),
    requestedThemeId: getDefaultThemeModeId(),
    clamped: false,
    scopeId: SETTINGS_THEME_SCOPE_ID
  };
}

function normalizeSettingsThemeScopeId(scopeId) {
  return String(scopeId || '').trim() === SETTINGS_THEME_SCOPE_ID
    ? SETTINGS_THEME_SCOPE_ID
    : SETTINGS_THEME_SCOPE_ID;
}

function normalizeSettingsThemeState(value) {
  const fallback = createDefaultSettingsThemeState();
  const normalizeThemeSelection = (requestedThemeId) => {
    const resolution = resolveThemeRef({ themeId: requestedThemeId });
    return {
      themeId: resolution?.themeRef?.themeId || fallback.themeId,
      requestedThemeId: String(requestedThemeId || '').trim().toLowerCase() || fallback.themeId,
      clamped: Boolean(resolution && resolution.clamped)
    };
  };
  if (typeof value === 'string') {
    const themeSelection = normalizeThemeSelection(value);
    return {
      version: 1,
      themeId: themeSelection.themeId,
      requestedThemeId: themeSelection.requestedThemeId,
      clamped: themeSelection.clamped,
      scopeId: SETTINGS_THEME_SCOPE_ID
    };
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return fallback;
  }

  if (value.version != null && Number(value.version) !== 1) {
    return fallback;
  }

  const themeSelection = normalizeThemeSelection(value.themeId);

  return {
    version: 1,
    themeId: themeSelection.themeId,
    requestedThemeId: themeSelection.requestedThemeId,
    clamped: themeSelection.clamped,
    scopeId: normalizeSettingsThemeScopeId(value.scopeId)
  };
}

function normalizeThemeModeId(themeId) {
  const resolution = resolveThemeRef({ themeId });
  return resolution?.themeRef?.themeId || getDefaultThemeModeId();
}

function hasLiveRoot(rootEl) {
  return Boolean(rootEl && rootEl.style && typeof rootEl.style.setProperty === 'function');
}

export function mapTokensToSettingsVars(tokens = {}) {
  const normalizedTokens = validateThemeTokens(tokens);
  const mapped = {};
  THEME_TOKEN_KEYS.forEach((tokenKey) => {
    const cssVarName = SETTINGS_THEME_VAR_KEYS[tokenKey];
    if (!cssVarName) {
      throw new Error(`Missing Settings theme CSS variable mapping for "${tokenKey}".`);
    }
    mapped[cssVarName] = normalizedTokens[tokenKey];
  });
  return mapped;
}

export function getPersistedSettingsThemeMode() {
  const storage = safeLocalStorage();
  if (!storage) return createDefaultSettingsThemeState();

  const raw = storage.getItem(SETTINGS_THEME_STORAGE_KEY);
  if (raw == null) return createDefaultSettingsThemeState();

  let normalized = createDefaultSettingsThemeState();
  let shouldRewrite = false;

  try {
    const text = String(raw).trim();
    if (!text) {
      shouldRewrite = true;
    } else if (text.startsWith('{')) {
      normalized = normalizeSettingsThemeState(JSON.parse(text));
      shouldRewrite = true;
    } else {
      normalized = normalizeSettingsThemeState(text);
      shouldRewrite = true;
    }
  } catch {
    normalized = createDefaultSettingsThemeState();
    shouldRewrite = true;
  }

  if (shouldRewrite) {
    try {
      storage.setItem(SETTINGS_THEME_STORAGE_KEY, JSON.stringify(normalized));
    } catch {
      // Ignore storage write failures; the normalized in-memory state is still valid.
    }
  }

  return normalized;
}

export function setPersistedSettingsThemeMode(themeState = createDefaultSettingsThemeState()) {
  const normalizedState = normalizeSettingsThemeState(themeState);
  const storage = safeLocalStorage();
  if (!storage) return normalizedState;

  try {
    storage.setItem(SETTINGS_THEME_STORAGE_KEY, JSON.stringify(normalizedState));
  } catch {
    // Ignore storage write failures and return the normalized state for callers.
  }

  return normalizedState;
}

export function resolveEffectiveSettingsThemeMode(state = {}) {
  const normalizedState = state && typeof state === 'object' ? state : {};
  const persistedState = normalizeSettingsThemeState({
    version: normalizedState.persistedVersion,
    themeId: normalizedState.persistedThemeModeId,
    scopeId: normalizedState.persistedScopeId
  });
  const appliedThemeModeId = normalizeThemeModeId(
    normalizedState.appliedThemeModeId || persistedState.themeId
  );
  const selectedThemeModeId = normalizeThemeModeId(
    normalizedState.selectedThemeModeId || appliedThemeModeId
  );
  const previewThemeModeId = isThemeModeId(normalizedState.previewThemeModeId)
    ? String(normalizedState.previewThemeModeId).trim().toLowerCase()
    : null;
  const selectedScopeId = normalizeSettingsThemeScopeId(
    normalizedState.selectedScopeId || persistedState.scopeId
  );
  const effectiveThemeModeId = previewThemeModeId
    || appliedThemeModeId
    || persistedState.themeId
    || getDefaultThemeModeId();

  return {
    persistedThemeModeId: persistedState.themeId,
    persistedScopeId: persistedState.scopeId,
    appliedThemeModeId,
    selectedThemeModeId,
    previewThemeModeId,
    effectiveThemeModeId,
    effectiveScopeId: selectedScopeId
  };
}

export function clearSettingsThemePreview(rootEl) {
  if (!rootEl || typeof rootEl !== 'object') return;
  if (rootEl.dataset && typeof rootEl.dataset === 'object') {
    delete rootEl.dataset.kgSettingsThemePreview;
  }
  if (typeof rootEl.removeAttribute === 'function') {
    rootEl.removeAttribute('data-kg-settings-theme-preview');
  }
}

export function applySettingsThemeMode(rootEl, themeId, options = {}) {
  if (!hasLiveRoot(rootEl)) {
    throw new Error('A live .kg-settings-root element is required to apply a Settings theme mode.');
  }

  const normalizedThemeId = normalizeThemeModeId(themeId);
  const themeDefinition = getThemeModeDefinition(normalizedThemeId);
  const settingsVars = mapTokensToSettingsVars(themeDefinition.tokens);

  Object.entries(settingsVars).forEach(([name, value]) => {
    rootEl.style.setProperty(name, value);
  });

  if (rootEl.dataset && typeof rootEl.dataset === 'object') {
    rootEl.dataset.kgSettingsThemeMode = normalizedThemeId;
    if (options.mode === 'preview') {
      rootEl.dataset.kgSettingsThemePreview = 'true';
    } else {
      delete rootEl.dataset.kgSettingsThemePreview;
    }
  }

  if (typeof rootEl.setAttribute === 'function') {
    rootEl.setAttribute('data-kg-settings-theme-mode', normalizedThemeId);
    if (options.mode === 'preview') {
      rootEl.setAttribute('data-kg-settings-theme-preview', 'true');
    } else {
      rootEl.removeAttribute?.('data-kg-settings-theme-preview');
    }
  }

  return {
    themeId: normalizedThemeId,
    scopeId: SETTINGS_THEME_SCOPE_ID,
    vars: settingsVars
  };
}

export function registerSettingsThemeConsumer(rootEl, options = {}) {
  if (!hasLiveRoot(rootEl)) {
    throw new Error('A live .kg-settings-root element is required to register the Settings theme consumer.');
  }

  let appliedThemeModeId = normalizeThemeModeId(options.appliedThemeModeId);
  let previewThemeModeId = null;
  const runtime = {
    rootElRef: rootEl
  };

  const consumerDefinition = {
    consumerId: SETTINGS_THEME_CONSUMER_ID,
    label: 'Shared Settings',
    kind: 'plugin',
    scopeGroup: 'settings',
    supportsThemeModes: true,
    priority: Number.isFinite(options.priority) ? Number(options.priority) : 100,
    rootElRef: runtime.rootElRef,
    applyThemeMode(nextThemeModeId, applyOptions = {}) {
      if (!hasLiveRoot(runtime.rootElRef)) {
        throw new Error('The Settings theme consumer is missing its current root element.');
      }
      const normalizedThemeModeId = normalizeThemeModeId(nextThemeModeId);
      const mode = applyOptions && applyOptions.mode === 'preview' ? 'preview' : 'apply';
      applySettingsThemeMode(runtime.rootElRef, normalizedThemeModeId, { mode });
      if (mode === 'preview') {
        previewThemeModeId = normalizedThemeModeId;
        return;
      }
      appliedThemeModeId = normalizedThemeModeId;
      previewThemeModeId = null;
      clearSettingsThemePreview(runtime.rootElRef);
    },
    clearThemePreview() {
      if (!hasLiveRoot(runtime.rootElRef) || previewThemeModeId == null) {
        return;
      }
      applySettingsThemeMode(runtime.rootElRef, appliedThemeModeId, { mode: 'apply' });
      previewThemeModeId = null;
      clearSettingsThemePreview(runtime.rootElRef);
    }
  };

  registerThemeConsumer(consumerDefinition);

  return {
    consumerId: SETTINGS_THEME_CONSUMER_ID,
    updateRootElRef(nextRootEl) {
      if (!hasLiveRoot(nextRootEl)) {
        throw new Error('A live .kg-settings-root element is required to refresh the Settings theme consumer root.');
      }
      runtime.rootElRef = nextRootEl;
      registerThemeConsumer({
        ...consumerDefinition,
        rootElRef: runtime.rootElRef
      });
    },
    unregister() {
      unregisterThemeConsumer(SETTINGS_THEME_CONSUMER_ID);
    }
  };
}

export function unregisterSettingsThemeConsumer() {
  unregisterThemeConsumer(SETTINGS_THEME_CONSUMER_ID);
}

function assertSettingsThemeAdapterContract() {
  if (Object.keys(SETTINGS_THEME_VAR_KEYS).length !== THEME_TOKEN_KEYS.length) {
    throw new Error('Settings theme variable mapping must cover the full canonical theme schema.');
  }
  const mapped = mapTokensToSettingsVars({ bg: 42 });
  if (Object.keys(mapped).length !== THEME_TOKEN_KEYS.length) {
    throw new Error('Settings theme variable mapping must stay complete.');
  }
  if (!Object.prototype.hasOwnProperty.call(mapped, '--kg-settings-bg')) {
    throw new Error('Settings theme variable mapping must include --kg-settings-bg.');
  }

  const touchedVars = [];
  const mockRootEl = {
    style: {
      setProperty(name, value) {
        touchedVars.push([name, value]);
      }
    },
    dataset: {},
    attributes: {},
    setAttribute(name, value) {
      this.attributes[name] = value;
    },
    removeAttribute(name) {
      delete this.attributes[name];
    }
  };

  applySettingsThemeMode(mockRootEl, 'default', { mode: 'preview' });

  if (touchedVars.length !== THEME_TOKEN_KEYS.length) {
    throw new Error('Applying a Settings theme mode must write the complete scoped variable set.');
  }
  if (mockRootEl.dataset.kgSettingsThemeMode !== 'default') {
    throw new Error('Applying a Settings theme mode must set data-kg-settings-theme-mode on the root.');
  }
}

assertSettingsThemeAdapterContract();
